import { captureMessage } from '@sentry/react';
import { generatePath } from 'react-router-dom';

import { PRODUCT_BASE_PATHS } from '@cast/console-constants';
import { getClusterIdFromLink } from 'features/cluster/routingUtils';
import { DateRangeFilter } from '@cast/shared-date-ui';

import {
  scalingPolicyDuplicateFromUrlKey,
  WORKLOAD_TABLE_SEARCH_URL_KEY,
} from './_constants';
import {
  RIGHTSIZING_QUERY_PARAM_KEYS,
  RightsizingFilterPreset,
} from './_hooks/useRightsizingTimeRangeSelection';
import { ScalingPolicyPaths } from './paths';

import { makeSearchQuery } from '@cast/shared-search-ui';
import { WORKLOAD_TABLE_SEARCH_WORKLOAD_NAME_KEY } from 'features/cluster/workload-optimization/workloads-list/TableSection/_hooks/useWorkloadsTableCriteria';

type OptimizationWorkloadsLinkArgs = {
  clusterId?: string;
  workloadName?: string;
};

const getClusterId = (defaultClusterId: string | undefined) => {
  const clusterId = defaultClusterId || getClusterIdFromLink();

  if (!clusterId) {
    console.error('Cluster id is required');
    captureMessage('Cluster id is required');
  }

  return clusterId;
};

export const makeOptimizationWorkloadsLink = ({
  clusterId: _clusterId,
  workloadName,
}: OptimizationWorkloadsLinkArgs = {}) => {
  const clusterId = getClusterId(_clusterId);

  const searchParams = makeSearchQuery({
    id: WORKLOAD_TABLE_SEARCH_URL_KEY,
    values: workloadName
      ? { [WORKLOAD_TABLE_SEARCH_WORKLOAD_NAME_KEY]: [workloadName] }
      : undefined,
  });
  const query = searchParams.toString() ? `?${searchParams.toString()}` : '';

  return `${PRODUCT_BASE_PATHS.automation}/external-clusters/${clusterId}/workload-optimization/workloads${query}`;
};

type OptimizationWorkloadLinkArgs = {
  workloadId: string;
  clusterId?: string;
  dateRangePreset?: RightsizingFilterPreset;
};

export const makeOptimizationWorkloadLink = ({
  workloadId,
  clusterId: _clusterId,
  dateRangePreset,
}: OptimizationWorkloadLinkArgs) => {
  const clusterId = getClusterId(_clusterId);

  const urlParams = new URLSearchParams();
  if (dateRangePreset) {
    urlParams.set(
      RIGHTSIZING_QUERY_PARAM_KEYS.dateRangePreset,
      dateRangePreset
    );
  }
  const urlParamsString = urlParams.toString();

  return `${PRODUCT_BASE_PATHS.automation}/external-clusters/${clusterId}/workload-optimization/workloads/${workloadId}?${urlParamsString}`;
};

const BASE_SCALING_POLICY_LINK = `${PRODUCT_BASE_PATHS.automation}/external-clusters/:clusterId/workload-optimization/scaling-policies`;

export const makeScalingPoliciesLink = () => {
  const clusterId = getClusterId(undefined);

  const urlParams = new URLSearchParams();

  return generatePath(`${BASE_SCALING_POLICY_LINK}?${urlParams.toString()}`, {
    clusterId,
  });
};

export const makeNewScalingPolicyLink = ({
  clusterId: _clusterId,
  duplicateFrom,
}: {
  duplicateFrom?: string;
  clusterId?: string;
} = {}) => {
  const clusterId = getClusterId(_clusterId);

  const urlParams = new URLSearchParams();
  if (duplicateFrom) {
    urlParams.set(scalingPolicyDuplicateFromUrlKey, duplicateFrom);
  }

  return generatePath(
    `${BASE_SCALING_POLICY_LINK}/new/${
      ScalingPolicyPaths.VPA_SETTINGS
    }?${urlParams.toString()}`,
    { clusterId }
  );
};

export const makeEditScalingPolicySettingsLink = ({
  clusterId: _clusterId,
  policyId,
}: {
  clusterId?: string;
  policyId: string;
}) => {
  const clusterId = getClusterId(_clusterId);

  return generatePath(
    `${BASE_SCALING_POLICY_LINK}/${policyId}/${ScalingPolicyPaths.VPA_SETTINGS}`,
    { clusterId }
  );
};

export const makeEditScalingPolicyAssignmentRulesLink = ({
  clusterId: _clusterId,
  policyId,
}: {
  clusterId?: string;
  policyId: string;
}) => {
  const clusterId = getClusterId(_clusterId);

  return generatePath(
    `${BASE_SCALING_POLICY_LINK}/${policyId}/assignment-rules`,
    {
      clusterId,
    }
  );
};

export const EVENT_LIST_VIEW_PARAM = 'event-log-tab';
export const EVENT_LIST_SEARCH_PARAM = 'woop-event-log';

type EventsLinkProps = {
  clusterId?: string;
  view?: 'event-list' | 'timeline';
  searchValues?: {
    workloadId?: string;
    dateRange?: {
      timezone?: string;
      preset?: DateRangeFilter;
      fromDate?: string;
      toDate?: string;
    };
  };
};

export const makeEventsLink = ({
  clusterId: defaultClusterId,
  view = 'timeline',
  searchValues,
}: EventsLinkProps = {}): string => {
  const clusterId = getClusterId(defaultClusterId);

  const searchParams = new URLSearchParams({
    [EVENT_LIST_VIEW_PARAM]: view,
  });

  makeSearchQuery({
    id: EVENT_LIST_SEARCH_PARAM,
    searchParams,
    values: searchValues,
  });

  return `${
    PRODUCT_BASE_PATHS.automation
  }/external-clusters/${clusterId}/workload-optimization/events?${searchParams.toString()}`;
};

type EventDetailsLinkArgs = {
  eventId: string;
  createdAt: string;
  clusterId?: string;
};

export const makeCustomMetricsLink = ({
  clusterId: _clusterId,
}: {
  clusterId?: string;
} = {}) => {
  const clusterId = getClusterId(_clusterId);

  return `${PRODUCT_BASE_PATHS.automation}/external-clusters/${clusterId}/workload-optimization/custom-metrics`;
};

export const makeEventDetailsLink = ({
  eventId,
  createdAt,
  clusterId: _clusterId,
}: EventDetailsLinkArgs) => {
  const clusterId = getClusterId(_clusterId);

  const urlParams = new URLSearchParams();
  urlParams.set('createdAt', createdAt);

  return `${
    PRODUCT_BASE_PATHS.automation
  }/external-clusters/${clusterId}/workload-optimization/events/${eventId}?${urlParams.toString()}`;
};

export const HPA_LIST_POLICY_PARAM = 'policy';

export const makeHpaListLink = ({
  clusterId: _clusterId,
  policyName,
}: {
  clusterId?: string;
  policyName?: string;
} = {}) => {
  const clusterId = getClusterId(_clusterId);

  const urlParams = new URLSearchParams();
  if (policyName) {
    urlParams.set(HPA_LIST_POLICY_PARAM, policyName);
  }
  const query = urlParams.toString() ? `?${urlParams.toString()}` : '';

  return `${PRODUCT_BASE_PATHS.automation}/external-clusters/${clusterId}/workload-optimization/hpa${query}`;
};
